import Phaser from 'phaser';
import { bus, Events } from './events';
import { Router } from './router';
import { SceneKeys } from './scenes/keys';
import { DEFAULT_SETTINGS, type Position, type Settings, type WeatherState } from './types';
import { SeededRng } from './rng';
import { StoryState, type StoryFlagId } from '@/story/flags';
import { resolveEntry } from '@/story/route';
import { SaveManager } from '@/save/SaveManager';
import { newSave, type SaveData, type SaveSlot } from '@/save/schema';

export interface UrlOptions { seed?: number; flag?: StoryFlagId; weather?: WeatherState; showcase?: string; nosave: boolean; fast: boolean }

/**
 * Process-wide game state shared by every scene through `scene.registry.get('ctx')` or the `ctx()` helper.
 * Owns story flags, save manager, settings, weather state, seed and the router.
 */
export class GameContext {
  readonly story: StoryState;
  readonly saves: SaveManager;
  readonly router: Router;
  readonly rng: SeededRng;
  seed: number;
  weather: WeatherState = 'sultry';
  settings: Settings;
  position: Position | null = null;
  playtimeMs = 0;
  private sessionStart = performance.now();
  /** Registered by the cinematic player: returns true if something was skipped. */
  skipHandler: (() => boolean) | null = null;
  /** Registered by the world/weather system so debug and saves can force weather. */
  weatherHandler: ((state: WeatherState) => void) | null = null;

  constructor(readonly game: Phaser.Game, readonly url: UrlOptions) {
    this.saves = new SaveManager(undefined, url.nosave);
    this.settings = { ...DEFAULT_SETTINGS, ...this.saves.readSettings() };
    if (url.fast) this.settings.textSpeed = 'instant';
    this.seed = url.seed ?? 1157;
    if (url.weather) this.weather = url.weather;
    this.rng = new SeededRng(this.seed);
    this.story = new StoryState();
    this.router = new Router(game);
    bus.onTyped(Events.STORY_FLAG, () => this.autosave());
  }

  static parseUrl(search: string): UrlOptions {
    const p = new URLSearchParams(search);
    const seed = p.get('seed');
    const flag = p.get('flag');
    return {
      seed: seed ? Number(seed) >>> 0 : undefined,
      flag: (flag as StoryFlagId | null) ?? undefined,
      weather: (p.get('weather') as WeatherState | null) ?? undefined,
      showcase: p.get('showcase') ?? undefined,
      nosave: p.get('nosave') === '1',
      fast: p.get('fast') === '1',
    };
  }

  /** Begin a brand-new game with a fresh seed and enter the route. */
  async newGame(): Promise<void> {
    this.story.reset();
    this.position = null;
    this.weather = 'sultry';
    this.playtimeMs = 0;
    this.sessionStart = performance.now();
    this.story.advance('TITLE');
    await this.enterRoute();
  }

  /** Load a slot and enter the legal scene for its flag. */
  async load(slot: SaveSlot): Promise<boolean> {
    const data = this.saves.read(slot);
    if (!data) return false;
    this.applySave(data);
    bus.emitTyped(Events.SAVE_LOADED, { slot });
    await this.enterRoute();
    return true;
  }

  applySave(data: SaveData): void {
    this.seed = data.seed;
    this.rng.setState(data.seed);
    this.story.reset();
    const last = data.story.flags[data.story.flags.length - 1];
    if (last) this.story.jumpTo(last as StoryFlagId);
    for (const m of data.story.marks) this.story.mark(m);
    this.position = data.position;
    this.weather = data.weather;
    this.playtimeMs = data.playtimeMs;
    this.sessionStart = performance.now();
  }

  toSave(existing?: SaveData | null): SaveData {
    const now = Date.now();
    const base = existing ?? newSave(this.seed, now);
    return {
      ...base,
      version: base.version,
      seed: this.seed,
      updatedAt: now,
      playtimeMs: this.playtimeMs + (performance.now() - this.sessionStart),
      story: this.story.snapshot(),
      position: this.position,
      weather: this.weather,
      settings: this.settings,
    };
  }

  autosave(): void {
    if (this.story.currentIndex() < 1) return; // nothing worth saving at TITLE
    this.saves.write('auto', this.toSave(this.saves.read('auto')));
  }
  saveManual(): boolean { return this.saves.write('manual-1', this.toSave(this.saves.read('manual-1'))); }

  async enterRoute(): Promise<void> {
    const entry = resolveEntry(this.story);
    await this.router.go(entry.scene, { spawn: entry.spawn, resumeFlag: entry.resumeFlag ?? undefined, position: this.position });
  }

  async jumpTo(flag: StoryFlagId): Promise<void> {
    this.story.jumpTo(flag);
    this.position = null;
    await this.enterRoute();
  }

  setWeather(state: WeatherState): void {
    this.weather = state;
    if (this.weatherHandler) this.weatherHandler(state);
    bus.emitTyped(Events.WEATHER_CHANGE, { state, t: 1 });
  }

  skipCinematic(): boolean { return this.skipHandler ? this.skipHandler() : false; }

  updateSettings(patch: Partial<Settings>): void {
    this.settings = { ...this.settings, ...patch };
    this.saves.writeSettings(this.settings);
    bus.emitTyped(Events.SETTINGS_CHANGE, this.settings);
  }

  async toTitle(): Promise<void> {
    await this.router.go(SceneKeys.TITLE, {});
  }

  listenerCount(): number {
    let n = bus.eventNames().reduce((acc, e) => acc + bus.listenerCount(e), 0);
    for (const s of this.game.scene.getScenes(false)) n += s.events.eventNames().reduce((acc, e) => acc + s.events.listenerCount(e), 0);
    return n;
  }
  timerCount(): number {
    return this.game.scene.getScenes(true).reduce((acc, s) => acc + ((s.time as unknown as { _active?: unknown[] })._active?.length ?? 0), 0);
  }
}

/** Helper for scenes: the shared context lives in the game registry under 'ctx'. */
export function ctxOf(scene: Phaser.Scene): GameContext {
  return scene.registry.get('ctx') as GameContext;
}
